import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import cn from "clsx";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import IntlInput from "../../../components/intlTelInput/intlTelInput";
import { SIGNUP_API_CODE_NAMES } from "../../../constants";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
  selectAcceptablePhoneLengths,
  selectCountryData,
  selectCountryShort,
  selectIsConflictMobileFlow,
  selectMobileCountryCode,
  selectMobileLocalNumberLength,
  selectRegistrationDataStatus,
  selectSettings,
  selectSignUpLoginToken
} from "../../../util/selectors";
import {
  initiateMobileVerification,
  resetAPIError,
  resetRegistrationDataStatus,
  setConflictMobileFlow,
  setSignUpLoginToken,
  setSocialLoginFlow,
  startRegistration
} from "../../../redux/actions/homepage.action";
import {
  useCountryId,
  useIsRTL,
  useLanguage,
  useRouteSlug
} from "../../../hooks";
import { SignUpData, SignUpFormValues } from "../types";
import AuthAnaltyicsEvent from "../../../services/analytics/main/authentication";
import useFormActivityHandler from "../../../services/analytics/hooks/useFormActivityHandler";
import { ButtonWithLoader } from "@/components/common/buttons";
import DialogModal from "@/components/dialogModal";
import { getAuthEmail } from "@/util/auth";
import { SIGN_UP_FLOW_STEP, SignUpContext } from "../signUp";
import {
  TurnstileCaptcha,
  TurnstileCaptchaRef
} from "@/components/captcha/captcha";

const SignUpForm = ({
  setOtpData,
  persistedValues,
  onPersist,
  setConflictModalOtpOpened
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const signUp = useAppSelector(state => state.homepage.signUp);
  const countryId = useCountryId();
  const mobileCountryCode = useAppSelector(selectMobileCountryCode);
  const defaultAreaCode = useAppSelector(state =>
    state.common.settings?.mobileLocalCode?.split(",")
  );
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isInteractiveCaptcha, setIsInteractiveCaptcha] = useState(false);
  const language = useLanguage();
  const countryData = useAppSelector(selectCountryData);
  const countrySHORT = useAppSelector(selectCountryShort);
  const mobileLocalNumberLength = useAppSelector(selectMobileLocalNumberLength);
  const acceptablePhoneLengths = useAppSelector(selectAcceptablePhoneLengths);
  const routeSlug = useRouteSlug();
  const registrationDataStatus = useAppSelector(selectRegistrationDataStatus);
  const signUpLoginToken = useAppSelector(selectSignUpLoginToken);
  const turnstileCaptchaToken = useAppSelector(
    state => state.authReducer.turnstileCaptchaToken
  );
  const settings = useAppSelector(selectSettings);
  const valueCaptchaFromCMS = settings?.captcha?.userRegisterScreen;
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const isAutomaticCaptcha =
    !captchaLoading &&
    !isInteractiveCaptcha &&
    valueCaptchaFromCMS &&
    !turnstileCaptchaToken;
  const shouldShowLoader = isAutomaticCaptcha || captchaLoading;

  const { setActiveFlowStep, setErrorMessage } = useContext(SignUpContext);

  const validatedUserEmail = getAuthEmail();

  const { t } = useTranslation("signUp");
  const [lastFocusedFormField, setLastFocusedField] = useState("");
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [showMobileConflictModal, setShowMobileConflictModal] = useState(false);
  const formikPhoneInputRef = useRef<HTMLInputElement | null>(null);
  const isConflictMobileFlow = useAppSelector(selectIsConflictMobileFlow);
  const [showVerifyButtonLoader, setShowVerifyButtonLoader] = useState(false);
  const formikState = useFormik({
    initialValues: { ...initialValues, ...persistedValues },
    enableReinitialize: true,
    onSubmit: async submitData => {
      setShowVerifyButtonLoader(true);
      try {
        onPersist?.({
          name: submitData.name.trim(),
          phone: submitData.phone,
          areaCode: submitData.areaCode,
          password: submitData.password
        });
        submitData.name = submitData.name.trim();
        dispatch(setSocialLoginFlow(false));
        isConflictMobileFlow && dispatch(setConflictMobileFlow(false));
        signUpLoginToken && dispatch(setSignUpLoginToken(""));
        const updatedSubmitData = getUserSignUpData(submitData);
        await startRegistrationProcess(updatedSubmitData);
      } catch (error) {
        console.error("Sign Up error", error);
        captchaRef.current.reset();
      } finally {
        setShowVerifyButtonLoader(false);
      }
    },
    validationSchema: () =>
      schema(
        Math.min(...acceptablePhoneLengths),
        Math.max(...acceptablePhoneLengths),
        t
      )
  });

  useFormActivityHandler({
    formName: "signup form",
    formikState,
    onErrors(errors) {
      AuthAnaltyicsEvent.signupFormError({
        errors
      });
    },
    lastFocusedFormField
  });

  const {
    resetForm,
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
    submitCount,
    isSubmitting
  } = formikState;

  const toggleShowPassword = () => setIsShowPassword(prevState => !prevState);

  const getPhoneNumberParts = useCallback(
    ({ phone, areaCode: phoneArea }: SignUpFormValues) => {
      const areaCode = phoneArea || get(defaultAreaCode, "[0]", "");
      return { mobileCountryCode, areaCode, phone };
    },
    [mobileCountryCode, defaultAreaCode]
  );

  const getUserSignUpData = useCallback(
    (values: SignUpFormValues): SignUpData => {
      const { name, password, isLoyaltyProgramChecked } = values;

      const { mobileCountryCode, areaCode, phone } =
        getPhoneNumberParts(values);

      return {
        fullName: name,
        email: validatedUserEmail,
        password,
        phone: `${mobileCountryCode}${areaCode}${phone}`,
        loyaltyConsent: isLoyaltyProgramChecked
      };
    },
    [signUp, language, getPhoneNumberParts, countryId]
  );

  const startRegistrationProcess = useCallback(
    async (data: SignUpData) => {
      const response = await dispatch(startRegistration(data));
      if (SIGNUP_API_CODE_NAMES.OTP_REQUIRED === response.code) {
        setOtpData(response);
        setActiveFlowStep(SIGN_UP_FLOW_STEP.OTP);
      }

      if (!response?.data?.success && response?.data?.message) {
        captchaRef.current.reset();
        setErrorMessage(response?.data?.message);
      }

      if (Number(response.code) === 200 && response.customerId) {
        history.push(`/${routeSlug}/registration/thank-you`);
      }
      return response;
    },
    [dispatch, history, routeSlug]
  );

  const isInputInvalid = (valueKey: string) =>
    Boolean(touched[valueKey] && errors && errors[valueKey]);

  const isShowGeneralError = useMemo(
    () => Object.entries(errors).length && submitCount,
    [errors, submitCount]
  );

  useEffect(() => {
    return () => {
      dispatch(resetAPIError());
      dispatch(resetRegistrationDataStatus());
      dispatch(setSocialLoginFlow(true));
    };
  }, []);

  useEffect(() => {
    if (
      registrationDataStatus.code ===
      SIGNUP_API_CODE_NAMES.MOBILE_ALREADY_USED_IN_ANOTHER_ACCOUNT
    ) {
      captchaRef.current.reset();
      dispatch(setConflictMobileFlow(true));
      setShowMobileConflictModal(true);
    }
  }, [registrationDataStatus]);

  useEffect(() => {
    if (
      isConflictMobileFlow &&
      registrationDataStatus.code === SIGNUP_API_CODE_NAMES.OTP_REQUIRED
    ) {
      setActiveFlowStep(SIGN_UP_FLOW_STEP.OTP);
    }
  }, [registrationDataStatus, isConflictMobileFlow]);

  useEffect(() => {
    resetForm();
    setIsShowPassword(false);
  }, [resetForm, countrySHORT]);

  const errorMessage = registrationDataStatus.message;

  const saveFocusedInputName = e => {
    const focusedInput = typeof e === "string" ? e : e.currentTarget.name;
    setLastFocusedField(focusedInput);
  };

  const handleResendOtp = async () => {
    const response = await dispatch(
      initiateMobileVerification(registrationDataStatus.token)
    );
    setOtpData(response);
    setConflictModalOtpOpened(true);
    setShowMobileConflictModal(false);
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="signUp">
        <div
          className={cn("form-group", {
            errorBorder: isInputInvalid("name")
          })}
        >
          <input
            type="text"
            className={"form-control"}
            name="name"
            placeholder={t("name")}
            value={values.name}
            onChange={handleChange}
            aria-label="Name"
            onFocus={saveFocusedInputName}
            autoComplete="off"
          />
          {isInputInvalid("name") && (
            <span className="errorMessage">
              {typeof errors.name === "string" ? errors.name : t("enterName")}
            </span>
          )}
        </div>

        <div
          className={cn("form-group e-pwd", {
            errorBorder: isInputInvalid("password")
          })}
        >
          <input
            type={isShowPassword ? "text" : "password"}
            className="form-control"
            name="password"
            placeholder={t("password")}
            value={values.password}
            onChange={handleChange}
            aria-label="Password"
            onFocus={saveFocusedInputName}
            autoComplete="off"
          />
          <span className="show-span" onClick={toggleShowPassword}>
            {isShowPassword ? `${t("hide")}` : `${t("show")}`}
          </span>
          {isInputInvalid("password") && (
            <span className="errorMessage">{t("passwordRequired")}</span>
          )}
        </div>

        <div
          className={cn("form-group", {
            errorBorder: isInputInvalid("phone")
          })}
        >
          {countrySHORT && (
            <IntlInput
              valid={true}
              phone={values.phone}
              areaCode={values.areaCode}
              handleCountryChange={(e, phone) => setFieldValue("phone", phone)}
              onSelectArea={area => setFieldValue("areaCode", area)}
              defaultCountry={countrySHORT}
              mobileLocalNumberLength={mobileLocalNumberLength}
              countryData={countryData}
              forwardedRef={formikPhoneInputRef}
              onFocus={saveFocusedInputName}
            />
          )}
          {isInputInvalid("phone") && (
            <span className="errorMessage">{t("phoneRequired")}</span>
          )}
        </div>

        {errorMessage &&
          registrationDataStatus.code !==
            SIGNUP_API_CODE_NAMES.MOBILE_ALREADY_USED_IN_ANOTHER_ACCOUNT && (
            <p className="errorMessage">{errorMessage}</p>
          )}

        {valueCaptchaFromCMS && (
          <TurnstileCaptcha
            ref={captchaRef}
            className="mt-4 mb-4"
            beforeLoading={() => setCaptchaLoading(true)}
            afterLoading={() => {
              setCaptchaLoading(false);
            }}
            onInterectiveChallangeStarted={() => {
              setIsInteractiveCaptcha(true);
            }}
            onToken={() => setCaptchaLoading(false)}
            onTokenExpired={() => setCaptchaLoading(false)}
          />
        )}

        <ButtonWithLoader
          type="submit"
          showLoaderWithoutClick={true}
          onClick={() => {
            AuthAnaltyicsEvent.authenticationMethod({
              isLogin: false,
              method: "email"
            });
          }}
          className="btn btn-default round_btn form_black_btn signUpButton"
          showLoader={
            isSubmitting || showVerifyButtonLoader || shouldShowLoader
          }
          disabled={isSubmitting || showVerifyButtonLoader || shouldShowLoader}
        >
          {t("signUp")}
        </ButtonWithLoader>

        <div className="form-group checkbox_wrap loyalty_checkbox">
          <input
            type="checkbox"
            className="custom_checkbox"
            name="isLoyaltyProgramChecked"
            checked={values.isLoyaltyProgramChecked}
            id="isLoyaltyProgramChecked"
            onChange={handleChange}
            onFocus={saveFocusedInputName}
          />
          <label className="checkbox_value" htmlFor="isLoyaltyProgramChecked">
            {t("agreeLoyalty")}
          </label>

          {isInputInvalid("isLoyaltyProgramChecked") && (
            <span className="errorMessage">{t("required")}</span>
          )}
        </div>
      </form>

      <DialogModal
        classname="mobile_conflict_modal"
        bodyText={errorMessage}
        cancelButtonText={t("cancel")}
        confirmButtonText={t("update")}
        showModal={showMobileConflictModal}
        onCancel={() => setShowMobileConflictModal(false)}
        onConfirm={handleResendOtp}
        showCaptcha={true}
        parentCaptchaRef={captchaRef}
      />
    </div>
  );
};

const schema = (minMobileLength: number, maxMobileLength: number, t) =>
  Yup.object().shape({
    name: Yup.string().min(3, t("min3Characters")).required(t("enterName")),
    password: Yup.string().min(8).max(32).trim().required(),
    phone: Yup.string().min(minMobileLength).max(maxMobileLength).required(),
    areaCode: Yup.string(),
    isLoyaltyProgramChecked: Yup.boolean().oneOf([true]).required()
  });

const initialValues: SignUpFormValues = {
  name: "",
  password: "",
  phone: "",
  areaCode: "",
  isLoyaltyProgramChecked: true
};

export default SignUpForm;
